import { UserManager, type UserManagerSettings } from 'oidc-client-ts';
import { useNotificationStore } from '../stores/notificationStore';

const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL;
const realm = import.meta.env.VITE_KEYCLOAK_REALM;
const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;

export const oidcSettings: UserManagerSettings = {
  authority: `${keycloakUrl}/realms/${realm}`,
  client_id: clientId,
  redirect_uri: window.location.origin + '/',
  post_logout_redirect_uri: window.location.origin + '/',
  scope: 'openid profile email',
  // Disabled deliberately. With a dead/expired refresh token, oidc-client-ts's
  // automaticSilentRenew hammers POST /realms/Dietech/.../token in a tight loop
  // (observed: 5,500+ 400s) and, combined with the old AuthGuard auto-redirect,
  // bounced users between the dashboard and the Keycloak login screen.
  // Renewal is ours instead, through ONE guarded, single-flight function
  // (auth/sessionRenewal.ts): the axios 401 interceptor in api/client.ts calls
  // it when a request fails, and auth/useSessionLifetime.ts calls it shortly
  // before the access token runs out while the user is active. Once a renewal
  // is refused the session is marked dead and nothing renews again.
  automaticSilentRenew: false,
};

/**
 * What Keycloak reports back (`kc_action_status`) after a `changePassword()`
 * round trip, as a message for the user. `null` when it is not that round trip.
 */
export function passwordChangeMessage(search: string): { text: string; type: 'success' | 'info' | 'error' } | null {
  switch (new URLSearchParams(search).get('kc_action_status')) {
    case 'success':
      return { text: 'Your password has been changed', type: 'success' };
    case 'cancelled':
      return { text: 'Password not changed', type: 'info' };
    case 'error':
      return { text: 'Your password could not be changed. Please try again.', type: 'error' };
    default:
      return null;
  }
}

/** Remove OIDC query params from the URL after login. */
export function onSigninCallback() {
  const message = passwordChangeMessage(window.location.search);
  if (message) useNotificationStore.getState().addNotification(message.text, message.type);
  window.history.replaceState({}, document.title, window.location.pathname);
}

/**
 * The signed-in user changes their own password, WinForms' Options "Change
 * Password". Keycloak's own update-password screen does it (an
 * application-initiated action): it asks for the current password when the
 * sign-in is not recent, enforces the realm's password policy, then sends the
 * user back here, where `onSigninCallback` reports how it went. Nothing
 * passes through our API, and no Keycloak admin account is needed.
 */
export function changePassword(): Promise<void> {
  return getUserManager().signinRedirect({ extraQueryParams: { kc_action: 'UPDATE_PASSWORD' } });
}

let userManager: UserManager | null = null;

/**
 * The one UserManager, shared by AuthProvider (App.tsx) and the renewal.
 *
 * Renewal calls userManager.signinSilent() directly instead of
 * react-oidc-context's auth.signinSilent(). The context wrapper dispatches
 * NAVIGATOR_INIT, which sets auth.isLoading, and AuthGuard renders the
 * LoadingScreen in place of the whole page while it is set: every renewal
 * through it unmounted the page the user was working on (unsaved edits lost).
 * It also swallows errors (returns null and sets auth.error), which flips
 * AuthGuard to the landing page. Called directly, a renewal changes nothing on
 * screen; success still reaches the context through the userLoaded event.
 */
export function getUserManager(): UserManager {
  if (!userManager) userManager = new UserManager(oidcSettings);
  return userManager;
}
